import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Divider,
  Alert,
  Fade,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  CircularProgress,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';

import {
  CloudUpload as UploadIcon,
  Image as ImageIcon,
  PlayArrow as AnalyzeIcon,
  CheckCircle as CheckIcon,
  Download as DownloadIcon,
  Psychology as PsychologyIcon,
  Timeline as TimelineIcon,
  Info as InfoIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Contrast as ContrastIcon,
  Brightness7 as BrightnessIcon,
} from '@mui/icons-material';

import { styled } from '@mui/material/styles';
import axios from 'axios';

const API_URL = '/api';

const UploadArea = styled(Paper)(({ theme, isDragging }) => ({
  padding: theme.spacing(4),
  textAlign: 'center',
  border: `2px dashed ${isDragging ? theme.palette.primary.main : theme.palette.divider}`,
  backgroundColor: isDragging ? theme.palette.action.hover : 'transparent',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  borderRadius: theme.spacing(2),
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.action.hover,
  },
}));

const ImagePreviewContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  maxHeight: 400,
  backgroundColor: theme.palette.grey[900],
  borderRadius: theme.spacing(1),
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  marginTop: theme.spacing(2),
}));

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`image-tabpanel-${index}`}
      aria-labelledby={`image-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const ImageAnalysis = () => {
  const navigate = useNavigate();

  const [selectedTab, setSelectedTab] = useState(0);
  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [logs, setLogs] = useState([]);
  const [imageUrl, setImageUrl] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [modelType, setModelType] = useState('chest-xray');
  const [patientId, setPatientId] = useState('');

  const fileInputRef = useRef(null);
  const imageRef = useRef(null);

  const modelOptions = [
    { value: 'chest-xray', label: 'Chest X-Ray' },
    { value: 'ct-scan', label: 'CT Scan' },
    { value: 'mri', label: 'MRI' },
    { value: 'pathology', label: 'Pathology Slide' },
  ];

  useEffect(() => {
    return () => {
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [imageUrl]);

  const handleFileUpload = useCallback((file) => {
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please upload a valid image file');
      return;
    }

    if (file.size > 20 * 1024 * 1024) {
      setError('File size exceeds 20MB limit');
      return;
    }

    if (imageUrl) {
      URL.revokeObjectURL(imageUrl);
    }

    setFile(file);
    setError(null);
    setResult(null);

    const url = URL.createObjectURL(file);
    setImageUrl(url);

    if (!patientId) {
      const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
      setPatientId(nameWithoutExt);
    }

    addLog(`📁 Uploaded: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)`);
  }, [imageUrl, patientId]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      handleFileUpload(droppedFile);
    }
  }, [handleFileUpload]);

  const handleFileInput = useCallback((e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      handleFileUpload(selectedFile);
    }
  }, [handleFileUpload]);

  const removeFile = useCallback(() => {
    if (imageUrl) {
      URL.revokeObjectURL(imageUrl);
    }
    setFile(null);
    setImageUrl(null);
    setResult(null);
    setError(null);
    setZoomLevel(1);
    setBrightness(100);
    setContrast(100);
    addLog('🗑️ Image removed');
  }, [imageUrl]);

  const handleZoomIn = useCallback(() => {
    setZoomLevel(prev => Math.min(prev + 0.25, 3));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoomLevel(prev => Math.max(prev - 0.25, 0.5));
  }, []);

  const addLog = useCallback((message) => {
    setLogs(prev => [...prev, {
      timestamp: new Date().toLocaleTimeString(),
      message,
    }]);
  }, []);

  const runAnalysis = useCallback(async () => {
    if (!file) {
      setError('Please upload an image file first');
      return;
    }

    setIsLoading(true);
    setProgress(0);
    setError(null);
    setResult(null);
    addLog(`🚀 Starting image analysis using ${modelOptions.find(m => m.value === modelType)?.label}...`);

    const formData = new FormData();
    formData.append('patient_id', patientId || 'unknown');
    formData.append('file', file);

    try {
      setProgress(20);
      addLog('📤 Uploading image file...');

      setProgress(40);
      addLog('🧠 Analyzing image with AI model...');

      const response = await axios.post(
        `${API_URL}/v1/predict/radiology`,
        formData,
        {
          headers: {
            'X-CTF-API-Key': 'test-key-123',
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total > 0) {
              const pct = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setProgress(Math.min(30 + pct * 0.7, 95));
            }
          },
        }
      );

      console.log('📥 Radiology API Response:', response.data);

      setProgress(100);
      const data = response.data;
      setResult(data);
      addLog(`✅ Analysis complete!`);
      addLog(`🎯 Prediction: ${data.predicted_label || 'Unknown'} (${(data.tb_probability * 100).toFixed(0)}% confidence)`);

    } catch (err) {
      console.error('Analysis error:', err);
      setError(err.response?.data?.detail || err.message || 'Analysis failed');
      addLog(`❌ Error: ${err.message}`);
    } finally {
      setIsLoading(false);
      setProgress(0);
    }
  }, [file, modelType, patientId, addLog]);

  const resetAnalysis = useCallback(() => {
    if (imageUrl) {
      URL.revokeObjectURL(imageUrl);
    }
    setFile(null);
    setResult(null);
    setError(null);
    setProgress(0);
    setLogs([]);
    setImageUrl(null);
    setZoomLevel(1);
    setBrightness(100);
    setContrast(100);
    setPatientId('');
    addLog('🔄 Analysis reset');
  }, [imageUrl, addLog]);

  const renderUploadArea = useCallback(() => (
    <UploadArea
      isDragging={isDragging}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={() => fileInputRef.current?.click()}
    >
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileInput}
        accept="image/*"
        style={{ display: 'none' }}
      />
      {file ? (
        <Box>
          <CheckIcon color="success" sx={{ fontSize: 60 }} />
          <Typography variant="h6">{file.name}</Typography>
          <Typography variant="body2" color="text.secondary">
            {(file.size / 1024 / 1024).toFixed(2)} MB
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {file.type}
          </Typography>
          <Button
            size="small"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={(e) => {
              e.stopPropagation();
              removeFile();
            }}
            sx={{ mt: 1 }}
          >
            Remove
          </Button>
        </Box>
      ) : (
        <Box>
          <UploadIcon sx={{ fontSize: 60, color: 'text.secondary' }} />
          <Typography variant="h6" color="text.secondary">
            Drag & drop an image file here
          </Typography>
          <Typography variant="body2" color="text.secondary">
            or click to browse (JPG, PNG, DICOM)
          </Typography>
        </Box>
      )}
    </UploadArea>
  ), [file, isDragging, handleDragOver, handleDragLeave, handleDrop, handleFileInput, removeFile]);

  const renderImagePreview = useCallback(() => (
    <ImagePreviewContainer>
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        {imageUrl && (
          <img
            ref={imageRef}
            src={imageUrl}
            alt="Uploaded medical image"
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              transform: `scale(${zoomLevel})`,
              filter: `brightness(${brightness}%) contrast(${contrast}%)`,
              transition: 'transform 0.3s ease, filter 0.3s ease',
              objectFit: 'contain',
            }}
          />
        )}
        {!imageUrl && (
          <Typography variant="body2" color="text.secondary">
            No image loaded
          </Typography>
        )}
      </Box>
    </ImagePreviewContainer>
  ), [imageUrl, zoomLevel, brightness, contrast]);

  const renderImageControls = useCallback(() => (
    <Paper sx={{ p: 2, mt: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        🎛️ Image Controls
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Zoom In">
            <IconButton onClick={handleZoomIn} size="small">
              <ZoomInIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Zoom Out">
            <IconButton onClick={handleZoomOut} size="small">
              <ZoomOutIcon />
            </IconButton>
          </Tooltip>
          <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', px: 1 }}>
            {`${(zoomLevel * 100).toFixed(0)}%`}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flex: 1 }}>
          <BrightnessIcon fontSize="small" />
          <Slider
            size="small"
            value={brightness}
            onChange={(e, v) => setBrightness(v)}
            min={50}
            max={150}
            sx={{ width: 100 }}
          />
          <ContrastIcon fontSize="small" />
          <Slider
            size="small"
            value={contrast}
            onChange={(e, v) => setContrast(v)}
            min={50}
            max={150}
            sx={{ width: 100 }}
          />
        </Box>
      </Box>
    </Paper>
  ), [zoomLevel, brightness, contrast, handleZoomIn, handleZoomOut]);

  const renderResults = useCallback(() => {
    if (!result) return null;

    const tbProb = (result.tb_probability || 0) * 100;
    const confidence = (result.confidence || 0) * 100;

    return (
      <Fade in={!!result} timeout={500}>
        <Box>
          <Card sx={{ mb: 3, bgcolor: result.predicted_label === 'TB' ? 'error.light' : 'success.light' }}>
            <CardContent>
              <Typography variant="h5" gutterBottom>
                🎯 Prediction: {result.predicted_label || 'Unknown'}
              </Typography>
              <Typography variant="h6">
                TB Probability: {tbProb.toFixed(1)}%
              </Typography>
              <LinearProgress
                variant="determinate"
                value={tbProb}
                sx={{ height: 12, borderRadius: 6, mt: 1 }}
              />
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Confidence: {confidence.toFixed(1)}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Model: {result.encoder_version || 'N/A'}
                </Typography>
              </Box>
            </CardContent>
          </Card>

          <Alert severity="info" sx={{ mt: 2 }}>
            {result.predicted_label === 'TB'
              ? `Model detected TB with ${tbProb.toFixed(1)}% probability.`
              : `Model detected normal with ${(100 - tbProb).toFixed(1)}% confidence.`}
          </Alert>
        </Box>
      </Fade>
    );
  }, [result]);

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              🩻 Image Analysis Lab
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Upload medical images for AI-powered diagnostic analysis
            </Typography>
          </Box>
          <Button variant="outlined" onClick={() => navigate('/platform')}>
            Back to Platform
          </Button>
        </Box>
        <Divider sx={{ mt: 2 }} />
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={5}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                📤 Upload Image
              </Typography>

              {renderUploadArea()}

              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}

              {file && (
                <Box sx={{ mt: 2 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Model</InputLabel>
                    <Select
                      value={modelType}
                      onChange={(e) => setModelType(e.target.value)}
                      label="Model"
                    >
                      {modelOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}

              {file && renderImagePreview()}
              {file && renderImageControls()}

              <Box sx={{ mt: 2 }}>
                <input
                  type="text"
                  placeholder="Patient ID"
                  value={patientId}
                  onChange={(e) => setPatientId(e.target.value)}
                  style={{
                    padding: '8px',
                    width: '100%',
                    border: '1px solid #ccc',
                    borderRadius: '5px',
                    marginBottom: '10px',
                  }}
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={runAnalysis}
                  disabled={!file || isLoading}
                  startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <AnalyzeIcon />}
                >
                  {isLoading ? 'Analyzing...' : 'Run Analysis'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={resetAnalysis}
                  disabled={isLoading}
                  startIcon={<RefreshIcon />}
                >
                  Reset
                </Button>
              </Box>

              {isLoading && (
                <Box sx={{ mt: 2 }}>
                  <LinearProgress variant="determinate" value={progress} />
                  <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                    {progress}% Complete
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={selectedTab} onChange={(e, v) => setSelectedTab(v)}>
                  <Tab label="📊 Results" />
                  <Tab label="📝 Logs" />
                </Tabs>
              </Box>

              <TabPanel value={selectedTab} index={0}>
                {renderResults()}
              </TabPanel>

              <TabPanel value={selectedTab} index={1}>
                <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                  {logs.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                      No logs yet. Run analysis to see the process.
                    </Typography>
                  ) : (
                    logs.map((log, idx) => (
                      <Box key={idx} sx={{ py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="caption" color="text.secondary">
                          [{log.timestamp}]
                        </Typography>
                        <Typography variant="body2" sx={{ ml: 1 }}>
                          {log.message}
                        </Typography>
                      </Box>
                    ))
                  )}
                </Box>
              </TabPanel>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ImageAnalysis;
